/**
 * StructuredAddressForm — Phase 32 Batch A.
 *
 * Input strutturato per indirizzo (ritiro / consegna):
 *   - via, numero civico, NPA, località, cantone, paese (Svizzera default)
 *   - azienda (opzionale), note di accesso (opzionale)
 *
 * Usa AddressAutocompleteInput per autocompletare e pre-compilare i campi strutturati.
 *
 * Props:
 *   value (object) — { street, house_number, zip, city, canton, country, company, access_notes, address, lat, lng }
 *   onChange(next) — emette l'intero oggetto
 *   testidPrefix (string) — prefisso per data-testid
 *   showCompany (boolean) — mostra campo azienda/reparto
 *
 * Validazione:
 *   Required: street, house_number, zip, city, canton
 *   Esposto via `isStructuredAddressValid(value)` helper.
 */
import { useEffect, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AddressAutocompleteInput from "@/components/AddressAutocompleteInput";
import { api, formatApiError } from "@/lib/api";

const CANTONS = [
  "TI",  // Ticino (default)
  "GR",  // Grigioni
];

const COUNTRIES = ["Svizzera"];

export function isStructuredAddressValid(v) {
  if (!v) return false;
  return Boolean(
    (v.street || "").trim() &&
    (v.house_number || "").trim() &&
    (v.zip || "").trim() &&
    (v.city || "").trim() &&
    (v.canton || "").trim()
  );
}

export default function StructuredAddressForm({
  value,
  onChange,
  testidPrefix = "addr",
  showCompany = false,
  legendLabel = "Indirizzo",
}) {
  const v = value || {};
  const [searchText, setSearchText] = useState(v.address || "");
  // Prefills may arrive after mount. Sync only a changed address string;
  // unrelated parent updates must never replace what the customer is typing.
  useEffect(() => { setSearchText(v.address || ""); }, [v.address]);
  // Phase 43.7 — "Usa la mia posizione" → reverse geocode → autofill struct
  const [geoBusy, setGeoBusy] = useState(false);

  const update = (patch) => onChange?.({ ...v, ...patch });
  // A manual correction must invalidate the address and pin selected previously.
  // Otherwise checkout can still use the old location behind the visible fields.
  const updateAddress = (patch) => {
    setSearchText("");
    update({ ...patch, address: "", lat: null, lng: null });
  };

  const useMyPosition = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Il tuo browser non supporta la geolocalizzazione.");
      return;
    }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.post("/maps/reverse-geocode", {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
          if (!data?.found) {
            toast.error("Non sono riuscito a riconoscere l'indirizzo dalla tua posizione. Inseriscilo manualmente o usa la ricerca.");
            return;
          }
          // canton uppercase, country fallback "Svizzera"
          const canton = (data.canton || v.canton || "TI").toUpperCase().slice(0, 4);
          update({
            address: data.formatted_address || "",
            lat: data.lat,
            lng: data.lng,
            street: data.street || "",
            house_number: data.house_number || "",
            zip: data.zip || "",
            city: data.city || "",
            canton,
            country: data.country || "Svizzera",
          });
          setSearchText(data.formatted_address || "");
          toast.success("Indirizzo compilato dalla tua posizione attuale.");
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Errore durante il riconoscimento dell'indirizzo. Riprova.");
        } finally {
          setGeoBusy(false);
        }
      },
      (geoErr) => {
        setGeoBusy(false);
        const code = geoErr?.code;
        if (code === 1) {
          toast.error("Hai negato l'accesso alla posizione. Abilita la geolocalizzazione dal lucchetto nella barra del browser e riprova.");
        } else if (code === 2) {
          toast.error("Posizione GPS non disponibile in questo momento. Controlla il segnale GPS e riprova.");
        } else if (code === 3) {
          toast.error("Timeout durante la lettura GPS. Riprova.");
        } else {
          toast.error("Impossibile rilevare la posizione. Inserisci l'indirizzo manualmente.");
        }
      },
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 10_000 },
    );
  };

  const handlePlace = (p) => {
    const s = p.struct || {};
    // Missing fields from a new place must be completed by the customer, not
    // inherited from the previously selected street or house number.
    update({
      address: p.address,
      lat: p.lat,
      lng: p.lng,
      street: s.street || "",
      house_number: s.house_number || "",
      zip: s.zip || "",
      city: s.city || "",
      canton: s.canton || v.canton || "TI",
      country: s.country || v.country || "Svizzera",
    });
    setSearchText(p.address || "");
  };

  return (
    <fieldset className="space-y-3" data-testid={`${testidPrefix}-fieldset`}>
      <legend className="overline text-dapnow-blue">{legendLabel}</legend>

      <div>
        <div className="flex items-end justify-between gap-2 mb-1">
          <label className="text-sm font-semibold tracking-tight">Cerca indirizzo</label>
          <button
            type="button"
            onClick={useMyPosition}
            disabled={geoBusy}
            data-testid={`${testidPrefix}-use-my-position`}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-dapnow-blue hover:underline disabled:opacity-60"
          >
            {geoBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <MapPin className="h-3 w-3" />}
            {geoBusy ? "Rilevo posizione…" : "Usa la mia posizione"}
          </button>
        </div>
        <AddressAutocompleteInput
          value={searchText}
          onChange={(e) => setSearchText(typeof e === "string" ? e : e?.target?.value || "")}
          onPlaceSelected={handlePlace}
          placeholder="Es. Via Nassa 5, Lugano"
          dataTestid={`${testidPrefix}-autocomplete`}
        />
        {v.address && !isStructuredAddressValid(v) && (
          <p className="mt-2 text-xs text-dapnow-muted" data-testid={`${testidPrefix}-address-confirmation`}>
            Indirizzo da confermare: <strong>{v.address}</strong>. Seleziona un risultato della ricerca oppure completa via, numero civico, NPA e località nei campi qui sotto.
          </p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="text-xs font-semibold tracking-tight">Via *</label>
          <input
            data-testid={`${testidPrefix}-street`}
            className="input-tall mt-1 w-full"
            value={v.street || ""}
            onChange={(e) => updateAddress({ street: e.target.value })}
            placeholder="Via Nassa"
          />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-tight">N° *</label>
          <input
            data-testid={`${testidPrefix}-house-number`}
            className="input-tall mt-1 w-full"
            value={v.house_number || ""}
            onChange={(e) => updateAddress({ house_number: e.target.value })}
            placeholder="5"
          />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-semibold tracking-tight">NPA *</label>
          <input
            data-testid={`${testidPrefix}-zip`}
            className="input-tall mt-1 w-full"
            value={v.zip || ""}
            onChange={(e) => updateAddress({ zip: e.target.value })}
            placeholder="6900"
            inputMode="numeric"
          />
        </div>
        <div className="col-span-2">
          <label className="text-xs font-semibold tracking-tight">Località *</label>
          <input
            data-testid={`${testidPrefix}-city`}
            className="input-tall mt-1 w-full"
            value={v.city || ""}
            onChange={(e) => updateAddress({ city: e.target.value })}
            placeholder="Lugano"
          />
        </div>
        <div>
          <label className="text-xs font-semibold tracking-tight">Cantone *</label>
          <select
            data-testid={`${testidPrefix}-canton`}
            className="input-tall mt-1 w-full"
            value={v.canton || "TI"}
            onChange={(e) => updateAddress({ canton: e.target.value })}
          >
            {CANTONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold tracking-tight">Paese</label>
          <select
            data-testid={`${testidPrefix}-country`}
            className="input-tall mt-1 w-full"
            value={v.country || "Svizzera"}
            onChange={(e) => updateAddress({ country: e.target.value })}
          >
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {showCompany && (
          <div>
            <label className="text-xs font-semibold tracking-tight">Azienda / Reparto</label>
            <input
              data-testid={`${testidPrefix}-company`}
              className="input-tall mt-1 w-full"
              value={v.company || ""}
              onChange={(e) => update({ company: e.target.value })}
              placeholder="Ragione sociale dell’azienda"
            />
          </div>
        )}
      </div>

      <div>
        <label className="text-xs font-semibold tracking-tight">Note di accesso (opz.)</label>
        <input
          data-testid={`${testidPrefix}-access-notes`}
          className="input-tall mt-1 w-full"
          value={v.access_notes || ""}
          onChange={(e) => update({ access_notes: e.target.value })}
          placeholder="Suonare al campanello 'Rossi', 2° piano"
        />
      </div>
    </fieldset>
  );
}
